import { Box, HStack, Avatar, Table, Tbody, Td, Text, Th, Thead, Tr, Flex } from '@chakra-ui/react';
import { constructQueryParams, getUserFullName } from '@duke-hq/lib';
import { SortableHeader } from '@duke-hq/ui/components/SortableHeader/SortableHeader';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useNavigate, useParams } from 'react-router-dom';
import React, { useCallback } from 'react';
import { StudentsURLParams } from './types';
import { djs } from '@duke-hq/djs';
import { SortOrder, SortOrderSchema } from '@duke-hq/lib/pagination';

interface ParticipantItem {
  id: string;
  first_name: string;
  last_name?: string | null;
  email: string | null;
  picture_url?: string | null;
  totalEnrolments?: number | null;
  last_activity_at?: Date | string | null;
  ownedBy?: {
    first_name: string;
    last_name?: string | null;
  } | null;
}

interface StudentsListProps {
  students: ParticipantItem[];
  urlParams: StudentsURLParams;
  onFilters: (filters: StudentsURLParams) => void;
}

const StudentListRow: React.FC<{
  user: ParticipantItem;
  onClick: (id: string) => void;
}> = React.memo(({ user, onClick }) => {
  const fullName = getUserFullName({
    first_name: user.first_name,
    last_name: user.last_name,
  });

  return (
    <Tr
      key={user.id}
      onClick={() => {
        onClick(user.id);
      }}
      cursor="pointer"
      _hover={{ bg: 'bg.subtle' }}
    >
      <Td>
        <HStack spacing="3">
          <Flex alignItems="center">
            <Avatar
              size="sm"
              mr={'12px'}
              name={fullName}
              src={user?.picture_url ?? undefined}
            ></Avatar>
            <Box>
              <Text fontWeight="medium">{fullName}</Text>
              <Text fontWeight="normal" color="fg.muted">
                {user.email}
              </Text>
            </Box>
          </Flex>
        </HStack>
      </Td>
      <Td>
        <Text fontWeight="normal" color="fg.muted">
          {user.totalEnrolments || 0}
        </Text>
      </Td>
      <Td>
        <Flex alignItems="start" direction={'column'}>
          <Text fontWeight="medium">
            {user?.ownedBy?.first_name + ' ' + user?.ownedBy?.last_name}
          </Text>
        </Flex>
      </Td>
      <Td>
        {user?.last_activity_at ? (
          <Text color="fg.muted">{djs().to(djs(user.last_activity_at))}</Text>
        ) : (
          <Text color="fg.muted">
            <Trans>Not logged in yet</Trans>
          </Text>
        )}
      </Td>
    </Tr>
  );
});

StudentListRow.displayName = 'StudentListRow';

export function StudentList({
  students = [],
  urlParams,
  onFilters,
}: StudentsListProps): React.JSX.Element {
  const items = students;
  const navigate = useNavigate();
  const { providerId } = useParams<{ providerId: string }>();
  const { orderBy } = urlParams;
  const sort = SortOrderSchema.safeParse(urlParams.sort).data;

  const handleClick = useCallback(
    (id: string | number) => {
      if (!providerId) {
        const baseUrl = `/student/${id}`;
        navigate(`${baseUrl}${constructQueryParams({ ...urlParams })}`);
      }
    },
    [navigate, providerId, urlParams],
  );

  const handleSort = useCallback(
    (name: string) => {
      const newSortDirection = sort === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC;
      const updatedFilters = {
        ...urlParams,
        orderBy: name,
        sort: newSortDirection,
      };

      onFilters(updatedFilters as StudentsURLParams);
    },
    [sort, urlParams, onFilters],
  );

  return (
    <Table>
      <Thead>
        <Tr>
          <Th>
            <SortableHeader
              label={t`Participant`}
              columnName="name"
              currentOrderBy={orderBy}
              currentSortOrder={sort}
              onSortChange={handleSort}
            />
          </Th>
          <Th>
            <SortableHeader
              label={t`Enrolments`}
              columnName="enrolments"
              currentOrderBy={orderBy}
              currentSortOrder={sort}
              onSortChange={handleSort}
            />
          </Th>
          <Th>
            <SortableHeader
              label={t`Owner`}
              columnName="consultants"
              currentOrderBy={orderBy}
              currentSortOrder={sort}
              onSortChange={handleSort}
            />
          </Th>
          <Th>
            <SortableHeader
              label={t`Last seen`}
              columnName="last_activity_at"
              currentOrderBy={orderBy}
              currentSortOrder={sort}
              onSortChange={handleSort}
            />
          </Th>
        </Tr>
      </Thead>
      <Tbody>
        {Array.isArray(items) && items.length
          ? items.map((student: ParticipantItem) => (
              <StudentListRow key={student.id} user={student} onClick={handleClick} />
            ))
          : null}
      </Tbody>
    </Table>
  );
}
